import { Box, Divider, Flex, Text, VStack } from '@chakra-ui/react'
import {
  DropdownField,
  FieldMap,
  FieldMapValue,
  FieldTypes,
  FileDBValue,
  objectToArray,
  UserGroup,
} from '@hb/shared'
import { FORM_ERROR } from 'final-form'

import { PopUpMessageContext } from '@hb/react'

import { useCallback, useContext, useMemo } from 'react'
import { useConsentFormsStatus } from '../../hooks'
import { assignFileAsConsentForm } from '../../hooks/backend/consentForms/utils'
import { FileView } from '../forms'
import { SimpleForm } from '../forms/FinalForm/SimpleForm'
import { DefaultModal } from '../Modals/DefaultModal'

interface AssignFileAsConsentFormModalProps {
  isOpen: boolean
  onClose: () => void
  file: FileDBValue
  fileId: string
  patientId: string
  assessmentId?: string | null
  accessLevel: UserGroup
}

export const AssignFileAsConsentFormModal = ({
  isOpen,
  onClose,
  file,
  fileId,
  patientId,
  assessmentId,
  accessLevel,
}: AssignFileAsConsentFormModalProps) => {
  const { processResponse } = useContext(PopUpMessageContext)

  // Get consent forms status for the patient
  const { consentForms } = useConsentFormsStatus(patientId, null, null)
  const unsignedConsentForms = useMemo(
    () => objectToArray(consentForms ?? {}).filter(form => !form.signedOn),
    [consentForms],
  )

  const consentFormField = useMemo<DropdownField | null>(
    () =>
      unsignedConsentForms.length
        ? {
            type: FieldTypes.DROPDOWN,
            placeholder: 'Select the consent form to assign this file to',
            options: unsignedConsentForms.map(form => ({
              text: form.name,
              id: form.id,
            })),
          }
        : null,
    [unsignedConsentForms],
  )

  const field = useMemo<FieldMap | null>(() => {
    if (!consentFormField) return null
    return {
      name: 'Assign File as Consent Form',
      children: {
        consentFormId: consentFormField,
      },
    }
  }, [consentFormField])

  const handleSubmit = useCallback(
    async (data: FieldMapValue) => {
      if (!file.storagePath) {
        return processResponse({ error: 'File storage path is missing' })
      }

      const { consentFormId } = data
      if (!consentFormId) {
        return processResponse({ error: 'Please select a consent form' })
      }

      try {
        await assignFileAsConsentForm({
          patientId,
          fileId,
          consentFormId,
          storagePath: file.storagePath,
          assessmentId: assessmentId ?? null,
          accessLevel,
        })

        onClose()
        return processResponse({ success: 'File assigned as consent form successfully' })
      } catch (err: any) {
        console.error(err)
        return processResponse({
          error: err?.message || 'Error assigning file as consent form',
        })
      }
    },
    [file, fileId, patientId, assessmentId, accessLevel, processResponse, onClose],
  )

  const initValue = useMemo(
    () => ({
      consentFormId: '',
    }),
    [],
  )

  if (!field) {
    return (
      <DefaultModal
        isOpen={isOpen}
        onClose={onClose}
        overlayHeader
        size="lg"
        render={() => (
          <Box>
            <VStack spacing={0} align="stretch">
              <Flex px={4} w="100%" borderBottom="1px solid #cdcdcd" py={2} bg="gray.100">
                <Text fontFamily="Hero-New" fontSize="lg" fontWeight={500}>
                  Assign File as Consent Form
                </Text>
              </Flex>
              <Box bg="gray.50" py={4} px={4}>
                <Text color="gray.600">No unsigned consent forms available for this patient.</Text>
              </Box>
            </VStack>
          </Box>
        )}
      />
    )
  }

  return (
    <DefaultModal
      isOpen={isOpen}
      onClose={onClose}
      overlayHeader
      size="lg"
      render={() => (
        <Box>
          <VStack spacing={0} align="stretch">
            <Flex px={4} w="100%" borderBottom="1px solid #cdcdcd" py={2} bg="gray.100">
              <Text fontFamily="Hero-New" fontSize="lg" fontWeight={500}>
                Assign File as Consent Form
              </Text>
            </Flex>
            <Box bg="gray.50" px={2} py={2}>
              <Box px={1}>
                <FileView field={null} data={file} />
              </Box>
              <Divider mb={2} />
              <SimpleForm
                field={field}
                value={initValue}
                boxProps={{
                  bg: 'transparent',
                  py: 0,
                  px: 0,
                  boxShadow: 'none',
                }}
                onSubmit={val =>
                  handleSubmit(val)
                    .then(res => (res.error ? { [FORM_ERROR]: res.error } : undefined))
                    .catch(err =>
                      processResponse({
                        error: err?.message || 'An error occurred',
                      }),
                    )
                }
              />
            </Box>
          </VStack>
        </Box>
      )}
    />
  )
}
